/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { type ReactElement, useId } from 'react';
import type { ShopperSearch } from '@/scapi';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

type VenueValue = NonNullable<ShopperSearch.schemas['ProductSearchRefinement']['values']>[number];

export type EatalyPlpVenueFilterOptionsProps = {
    values: VenueValue[];
    selectedValue: string | null;
    isPending?: boolean;
    onSelect: (value: string | null) => void;
    ariaLabel: string;
    className?: string;
    idPrefix?: string;
};

export default function EatalyPlpVenueFilterOptions({
    values,
    selectedValue,
    isPending = false,
    onSelect,
    ariaLabel,
    className,
    idPrefix: idPrefixProp,
}: EatalyPlpVenueFilterOptionsProps): ReactElement {
    const generatedIdPrefix = useId();
    const idPrefix = idPrefixProp ?? `venue-${generatedIdPrefix}`;

    return (
        <RadioGroup
            value={selectedValue ?? ''}
            onValueChange={(next) => {
                if (selectedValue && next === selectedValue) {
                    onSelect(null);
                    return;
                }
                onSelect(next);
            }}
            aria-label={ariaLabel}
            className={cn('flex flex-col gap-6', isPending && 'pointer-events-none opacity-50', className)}
            data-testid="plp-venue-filter-options">
            {values.map((option, index) => {
                const optionValue = option.value;
                const optionLabel = option.label || option.value;
                const optionId = `${idPrefix}-${index}`;
                const isSelected = selectedValue === optionValue;

                return (
                    <label
                        key={optionValue}
                        htmlFor={optionId}
                        className="flex w-full cursor-pointer items-start gap-2 rounded-sm pr-6 has-[:focus-visible]:outline-solid has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring"
                        onClick={(event) => {
                            // RadioGroup does not re-fire onValueChange for the active value;
                            // clicking the selected row clears the venue filter.
                            if (isSelected) {
                                event.preventDefault();
                                onSelect(null);
                            }
                        }}>
                        <span className="flex size-6 shrink-0 items-center justify-center p-1">
                            <RadioGroupItem value={optionValue} id={optionId} className="shrink-0" />
                        </span>
                        <span className="body-copy-m shrink-0 text-left text-neutral-10">{optionLabel}</span>
                        {option.hitCount != null ? (
                            <span className="body-copy-m min-w-0 flex-1 text-left text-muted-foreground">
                                {option.hitCount}
                            </span>
                        ) : null}
                    </label>
                );
            })}
        </RadioGroup>
    );
}
